/* eslint-disable no-unused-vars */
import React, { useEffect } from 'react';
import clsx from 'clsx';
import { useSelector, useDispatch } from 'react-redux';
import {
  Box,
  Dialog,
  DialogContent,
  Tab,
  Tabs,
  Table,
  TableContainer,
  TableBody,
  TableRow,
  TableCell,
  Paper,
  Typography,
  Grid,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import {
  AccountCircle as AccountCircleIcon,
  CheckCircle as SuccessIcon,
  RemoveCircle as PartialIcon,
  Error as FailureIcon,
} from '@material-ui/icons';
import {
  red,
  yellow,
  green,
  grey,
} from '@material-ui/core/colors';
import BaseButton from '../common/BaseButton';
import StatusIcon from '../common/StatusIcon';
import MatchTooltip from '../common/MatchTooltip';
import CopyToClipboard from '../common/CopyToClipboard';

import {
  selectGuest,
  selectCompany,
  selectIdentity,
  selectCompanyCode,
  selectReservationCode,
  selectDocumentImages,
  selectPersonalData,
  selectFailReason,
  selectLivenessError,
  selectLivenessScore,
  selectLivenessCheck,
  selectFirstNameCheck,
  selectAgeMinimumCheck,
  selectAuthenticationScore,
  selectDisableIdentityVerification,
  getSessionByReservation,
  createVerificationSession,
} from '../../store/widgetSlice';

import {
  setMessage,
} from '../../store/authSlice';

const useStyles = makeStyles((theme) => ({
  container: {
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2),
    width: 450,
    maxWidth: '100%',
  },
  panel: {
    backgroundColor: theme.palette.background.paper,
  },
  status: {
    fontWeight: 600,
  },
  details: {
    maxWidth: 260,
    fontSize: 14,
    marginBottom: theme.spacing(2),
    textAlign: 'center',
  },
  link: {
    maxWidth: 400,
    marginBottom: theme.spacing(2),
    textAlign: 'center',
  },
  selfie: {
    width: 150,
    height: 150,
    borderRadius: '50%',
    objectFit: 'cover',
    objectPosition: 'center',
  },
  document: {
    width: 200,
    height: 150,
    objectFit: 'cover',
    objectPosition: 'center',
    maxWidth: '100%',
  },
  bold: {
    fontWeight: 600,
  },
  cell: {
    height: 30,
    padding: 0,
    paddingTop: theme.spacing(2),
    paddingBottom: theme.spacing(2),

    'tr:last-child &': {
      borderBottom: 0,
    },
  },
  badge: {
    position: 'absolute',
    top: -theme.spacing(1),
    right: -theme.spacing(1),
    borderRadius: '50%',
    background: theme.palette.background.paper,
  },
  elementHidden: {
    display: 'none',
  },
}));

function compareNames(name1, name2) {
  return typeof name1 === 'string'
    && typeof name2 === 'string'
    && name1.toLowerCase() === name2.toLowerCase();
}

function TabPanel(props) {
  const {
    children,
    value,
    index,
    ...other
  } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box>
          <Typography>{children}</Typography>
        </Box>
      )}
    </div>
  );
}

function VerificationStatus({ classes }) {
  const dispatch = useDispatch();
  const identity = useSelector(selectIdentity);
  const failReason = useSelector(selectFailReason);
  const livenessCheck = useSelector(selectLivenessCheck);
  const livenessError = useSelector(selectLivenessError);
  const firstNameCheck = useSelector(selectFirstNameCheck);
  const ageMinimumCheck = useSelector(selectAgeMinimumCheck);
  const authenticationScore = useSelector(selectAuthenticationScore);
  const guest = useSelector(selectGuest);
  const company = useSelector(selectCompany);
  const personalData = useSelector(selectPersonalData);
  const companyCode = useSelector(selectCompanyCode);
  const reservationCode = useSelector(selectReservationCode);
  const fullNameCheck = (((compareNames(guest?.firstName, personalData?.firstName) || firstNameCheck)
  && compareNames(guest?.lastName, personalData?.lastName)));

  const handleClick = ((event) => {
    event.preventDefault();

    if (companyCode && reservationCode) {
      dispatch(createVerificationSession({ companyCode, reservationCode }))
        .unwrap()
        .then(() => {
          dispatch(setMessage({ message: 'Session created', severity: 'success' }));
        })
        .catch(() => {
          dispatch(setMessage({ message: 'Session could not be created', severity: 'error' }));
        });
    }
  });

  if (!identity) {
    return (
      <Box align="center">
        <Box mt={6}>
          <Typography className={classes.status} component="h3" variant="h6">
            User verification has not been started
          </Typography>
        </Box>
        <Box my={6}>
          <PartialIcon style={{ color: grey[500], fontSize: 80 }} />
          <Typography className={classes.details} component="p" variant="body1">
            An email and SMS message will be sent to the guest with a verification link.
          </Typography>
        </Box>
        <Box my={6}>
          <BaseButton
            IconComponent={AccountCircleIcon}
            onClick={handleClick}
          >
            Begin Verification
          </BaseButton>
        </Box>
      </Box>
    );
  }

  if (identity && identity.status === 'E') {
    return (
      <Box align="center">
        <Box mt={6}>
          <Typography className={classes.status} component="h3" variant="h6">
            User verification has expired
          </Typography>
        </Box>
        <Box my={6}>
          <PartialIcon style={{ color: yellow[500], fontSize: 80 }} />
          <Typography className={classes.details} component="p" variant="body1">
            More than 6 months have passed since this session was created.
          </Typography>
        </Box>
        <Box my={6}>
          <BaseButton
            IconComponent={AccountCircleIcon}
            onClick={handleClick}
          >
            Begin New Verification
          </BaseButton>
        </Box>
      </Box>
    );
  }

  if (identity && identity.status === 'P') {
    return (
      <Box align="center">
        <Box mt={6}>
          <Typography className={classes.status} component="h3" variant="h6">
            User verification is pending
          </Typography>
        </Box>
        <Box my={6}>
          <PartialIcon style={{ color: yellow[500], fontSize: 80 }} />
          <Typography className={classes.details} component="p" variant="body1">
            A verification link has been sent to the guest.
          </Typography>
          <Typography className={classes.link} component="p" variant="body1">
            <CopyToClipboard
              text={(identity?.response?.customDomain || company?.customAttributes?.customDomain)
                // eslint-disable-next-line max-len
                ? `${identity?.response?.customDomain || company?.customAttributes?.customDomain}/verify/?reference=${identity.reference}`
                : `${process.env.REACT_APP_BASE_URL}/verify/${identity.reference}`}
            />
          </Typography>
        </Box>
        <Box my={6}>
          <BaseButton
            IconComponent={AccountCircleIcon}
            onClick={handleClick}
          >
            Retry Verification
          </BaseButton>
        </Box>
      </Box>
    );
  }

  if (identity && identity.status === 'C') {
    return (
      <Box align="center">
        <Box mt={6}>
          <Typography className={classes.status} component="h3" variant="h6">
            User verification has been completed
          </Typography>
        </Box>
        <Box my={6}>
          <SuccessIcon style={{ color: green[500], fontSize: 80 }} />
          <Typography className={classes.details} component="p" variant="body1">
            Guest information matches the ID provided.
          </Typography>
        </Box>
        <Box my={6}>
          <BaseButton
            IconComponent={AccountCircleIcon}
            onClick={handleClick}
          >
            Retry Verification
          </BaseButton>
        </Box>
      </Box>
    );
  }

  return (
    <Box align="center">
      <Box mt={6}>
        <Typography align="center" className={classes.status} component="h3" variant="h6">
          User verification has been completed
        </Typography>
      </Box>
      <Box my={6}>
        <FailureIcon style={{ color: red[500], fontSize: 80 }} />
        { ((identity && identity.status !== 'C') && (failReason || livenessError))
          && (
          <Typography align="center" className={classes.details} component="p" variant="body1">
            <b>Facial Recognition Results:</b>
            {
            (!failReason && !livenessError)
              ? (
                <>
                  <br />
                  <span>Selfie matches the photo on the ID provided.</span>
                </>
              )
              : ('')
            }
            {
              (failReason && !livenessError)
                ? (
                  <>
                    <br />
                    <span>
                      {`${failReason} Go to the Photos tab for more information.`}
                    </span>
                  </>
                )
                : ('')
            }
            {
              (livenessCheck === false && !livenessError)
                ? (
                  <>
                    <br />
                    <span>
                      Face liveness check not passed. Go to the Details tab to see score.
                    </span>
                  </>
                )
                : ('')
            }
            {
              (livenessCheck === false && livenessError)
                ? (
                  <>
                    <br />
                    <span>
                      {`The following error was encountered during liveness check: ${livenessError}.`}
                    </span>
                  </>
                )
                : ('')
            }
          </Typography>
          )}
        { (identity && identity.status !== 'C') && (!fullNameCheck || (ageMinimumCheck === false))
              && (
              <Typography align="center" className={classes.details} component="p" variant="body1">
                <b>ID Recognition Results:</b>
                <br />
                <span>
                  {
                    (fullNameCheck && (ageMinimumCheck === true))
                      ? 'Name on the ID did not match guest information. Go to the Details tab to review names.'
                      : ''
                  }
                </span>
                <span>
                  {
                    (!fullNameCheck)
                      ? 'Name on the ID did not match guest information. Go to the Details tab to review names.'
                      : ''
                  }
                </span>
                <br />
                <span>
                  {
                    (ageMinimumCheck === false)
                      ? 'Age minimum set for this reservation not met. Go to the Details tab to see DOB.'
                      : ''
                  }
                </span>
              </Typography>
              )}
      </Box>
      <Box my={6}>
        <BaseButton
          IconComponent={AccountCircleIcon}
          onClick={handleClick}
        >
          Retry Verification
        </BaseButton>
      </Box>
    </Box>
  );
}

export default function RecognizeModal({
  hideDefaultButton,
  externalOpen,
  externalHandleClose,
}) {
  const classes = useStyles();
  const dispatch = useDispatch();
  const guest = useSelector(selectGuest);
  const companyCode = useSelector(selectCompanyCode);
  const reservationCode = useSelector(selectReservationCode);
  const identity = useSelector(selectIdentity);
  const documentImages = useSelector(selectDocumentImages);
  const personalData = useSelector(selectPersonalData);
  const livenessError = useSelector(selectLivenessError);
  const livenessScore = useSelector(selectLivenessScore);
  const firstNameCheck = useSelector(selectFirstNameCheck);
  const authenticationScore = useSelector(selectAuthenticationScore);
  const disableIdentityVerification = useSelector(selectDisableIdentityVerification);

  const [open, setOpen] = React.useState(false);
  const [currentTab, setCurrentTab] = React.useState(0);

  const handleTabChange = (event, newValue) => {
    setCurrentTab(newValue);
  };

  const handleClickOpen = () => {
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
  };

  useEffect(() => {
    if (companyCode && reservationCode) {
      dispatch(getSessionByReservation({ companyCode, reservationCode }));
    }
  }, [dispatch, companyCode, reservationCode]);

  useEffect(() => {
    setOpen(externalOpen);
  }, [externalOpen]);

  return (
    <div>
      {
        !hideDefaultButton
        && (
          <BaseButton
            IconComponent={AccountCircleIcon}
            onClick={handleClickOpen}
            fullWidth
            disabled={disableIdentityVerification}
          >
            Identity Check
            <StatusIcon
              className={classes.badge}
              status={(!(identity && (identity.status === 'P' || identity.status === 'E')))
                ? guest && guest.identityVerified
                : 2}
            />
          </BaseButton>
        )
      }
      <Dialog
        className={classes.dialog}
        open={open}
        onClose={externalHandleClose || handleClose}
        maxWidth="sm"
      >
        <Paper>
          <Tabs
            value={currentTab}
            indicatorColor="primary"
            textColor="primary"
            onChange={handleTabChange}
            variant="fullWidth"
          >
            <Tab label="Verification Status" />
            <Tab label="Photos" disabled={!identity || (identity.status === 'P' || identity.status === 'E')} />
            <Tab label="Details" disabled={!identity || (identity.status === 'P' || identity.status === 'E')} />
          </Tabs>
        </Paper>
        <DialogContent>
          <TabPanel className={classes.panel} value={currentTab} index={0}>
            <VerificationStatus classes={classes} />
          </TabPanel>
          <TabPanel
            className={classes.panel}
            value={currentTab}
            index={1}
          >
            <Box align="center">
              <Box mt={3}>
                <Typography className={classes.status} component="h3" variant="h6">
                  Document Images
                </Typography>
              </Box>
              <Box my={4}>
                {documentImages && documentImages.filter((obj) => obj.type === '1').map((obj) => (
                  <img className={classes.selfie} src={obj.url} alt="Selfie" />
                ))}
              </Box>
              <Box display="flex" mb={4}>
                {documentImages && documentImages.filter((obj) => obj.type === '0').map((obj, index) => (
                  <Grid xs={6} item mx={1}>
                    <img className={classes.document} src={obj.url} alt={`Document ${index}`} />
                  </Grid>
                ))}
              </Box>
            </Box>
          </TabPanel>
          <TabPanel
            className={classes.panel}
            value={currentTab}
            index={2}
          >
            <Box m={3}>
              <TableContainer className={classes.container}>
                <Table className={classes.table}>
                  <TableBody>
                    <TableRow className={classes.row}>
                      <TableCell className={[classes.cell, classes.bold]} align="left">
                        Reference No.
                      </TableCell>
                      <TableCell className={classes.cell} align="right">
                        {identity && identity.reference}
                      </TableCell>
                      <TableCell className={classes.cell} align="right" />
                    </TableRow>
                    <TableRow className={classes.row}>
                      <TableCell className={[classes.cell, classes.bold]} align="left">
                        Document No.
                      </TableCell>
                      <TableCell className={classes.cell} align="right">
                        {personalData && personalData.documentNumber}
                      </TableCell>
                      <TableCell className={classes.cell} align="right" />
                    </TableRow>
                    <TableRow className={clsx((!authenticationScore) && classes.elementHidden, classes.row)}>
                      <TableCell className={[classes.cell, classes.bold]} align="left">
                        Score
                      </TableCell>
                      <TableCell className={classes.cell} align="right">
                        {(authenticationScore) ? `${authenticationScore * 100}%` : 'N/A'}
                      </TableCell>
                      <TableCell className={classes.cell} align="right">
                        <StatusIcon
                          className={classes.tableIcon}
                          status={(authenticationScore && authenticationScore >= 0.6) ? 3 : 1}
                        />
                      </TableCell>
                    </TableRow>
                    <TableRow className={classes.row}>
                      <TableCell className={[classes.cell, classes.bold]} align="left">
                        First Name
                      </TableCell>
                      <TableCell className={classes.cell} align="right">
                        {personalData && personalData.firstName}
                      </TableCell>
                      <TableCell className={classes.cell} align="right">
                        <MatchTooltip
                          guest={guest && guest.firstName}
                          match={personalData && personalData.firstName}
                        >
                          <StatusIcon
                            className={classes.tableIcon}
                            status={(guest && personalData
                              && (compareNames(guest.firstName, personalData.firstName) || firstNameCheck)) ? 3 : 1}
                          />
                        </MatchTooltip>
                      </TableCell>
                    </TableRow>
                    <TableRow className={classes.row}>
                      <TableCell className={[classes.cell, classes.bold]} align="left">
                        Last Name
                      </TableCell>
                      <TableCell className={classes.cell} align="right">
                        {personalData && personalData.lastName}
                      </TableCell>
                      <TableCell className={classes.cell} align="right">
                        <MatchTooltip
                          guest={guest && guest.lastName}
                          match={personalData && personalData.lastName}
                        >
                          <StatusIcon
                            className={classes.tableIcon}
                            status={(guest && personalData
                              && compareNames(guest.lastName, personalData.lastName)) ? 3 : 1}
                          />
                        </MatchTooltip>
                      </TableCell>
                    </TableRow>
                    <TableRow className={classes.row}>
                      <TableCell className={[classes.cell, classes.bold]} align="left">
                        Date of Birth
                      </TableCell>
                      <TableCell className={classes.cell} align="right">
                        {personalData && personalData.dob}
                      </TableCell>
                      <TableCell className={classes.cell} align="right" />
                    </TableRow>
                    <TableRow className={classes.row}>
                      <TableCell className={[classes.cell, classes.bold]} align="left">
                        Nationality
                      </TableCell>
                      <TableCell className={classes.cell} align="right">
                        {personalData && personalData.nationalityIso2}
                      </TableCell>
                      <TableCell className={classes.cell} align="right" />
                    </TableRow>
                    <TableRow className={classes.row}>
                      <TableCell className={[classes.cell, classes.bold]} align="left">
                        Address
                      </TableCell>
                      <TableCell className={classes.cell} align="right">
                        {(personalData && personalData.address1) || 'N/A'}
                      </TableCell>
                      <TableCell className={classes.cell} align="right" />
                    </TableRow>
                    <TableRow
                      className={clsx((!livenessScore && !livenessError) && classes.elementHidden, classes.row)}
                    >
                      <TableCell className={[classes.cell, classes.bold]} align="left">
                        Liveness
                      </TableCell>
                      <TableCell className={classes.cell} align="right">
                        {
                          (!livenessError)
                            ? `${(parseFloat(livenessScore || 0) * 100).toFixed(2)}%`
                            : livenessError
                        }
                      </TableCell>
                      <TableCell className={classes.cell} align="right" />
                    </TableRow>
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          </TabPanel>
        </DialogContent>
      </Dialog>
    </div>
  );
}
